import { useCreateRole, useUpdateRole, useGetRoles } from "api/roles/service";
import React, { useState, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Card,
    CardHeader,
    CardBody,
    CardTitle,
    Row,
    Col,
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Spinner,
} from "reactstrap";
import RolePermissions from "components/RolePermissions";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { useTheme2FormPage, Theme2FormFieldsStyle, THEME2_ADMIN_INSETS } from "hooks/useTheme2FormPage";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";
import { useTranslation } from "react-i18next";

const RolesAdd = ({ activeColor = "secondary", shellThemeVariant = "1" }) => {
    const { t } = useTranslation();
    const isTheme2 = shellThemeVariant === "2";
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const {
        inputStyle,
        inputClassName,
        theme2FieldsCss,
        contentClassName,
        formFieldsClassName,
    } = useTheme2FormPage({
        shellThemeVariant,
        pageKey: "roles-add",
        insetClass: THEME2_ADMIN_INSETS.roles,
    });
    const rolesTheme2Css = useMemo(() => {
        if (!isTheme2) return theme2FieldsCss;
        const fontSize = THEME2_SIDEBAR_NAV_FONT_SIZE_VAR;
        const pageScope = ".roles-add-page.roles-add-page--theme2";
        return `${theme2FieldsCss}
          ${pageScope} label,
          ${pageScope} .form-control-label,
          ${pageScope} .form-control,
          ${pageScope} textarea.form-control,
          ${pageScope} .btn,
          ${pageScope} .btn-sm,
          ${pageScope} .invalid-feedback,
          ${pageScope} .card-header h2,
          ${pageScope} .card-header h4,
          ${pageScope} .roles-add-card-title,
          ${pageScope} .roles-add-form-fields .card .card-header,
          ${pageScope} .role-permissions-section--theme2 small,
          ${pageScope} .role-permissions-section--theme2 label,
          ${pageScope} .role-permissions-section--theme2 .btn,
          ${pageScope} .role-permissions-section--theme2 .btn-sm,
          ${pageScope} .role-permissions-section--theme2 .role-permissions-section__toggle,
          ${pageScope} .role-permissions-section--theme2 .role-permissions-section__title,
          ${pageScope} .role-permissions-section--theme2 .role-permissions-group-title,
          ${pageScope} .role-permissions-section--theme2 .card-header {
            font-size: ${fontSize} !important;
          }`;
    }, [isTheme2, theme2FieldsCss]);
    
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const navigate = useNavigate();
    const createRoleMutation = useCreateRole();
    const updateRoleMutation = useUpdateRole();
    const { data: rolesData } = useGetRoles(undefined, { enabled: isEditMode });

    const [formData, setFormData] = useState({
        documentId: '',
        name: '',
        description: '',
        permissions: {}, // now an object
    });

    const [errors, setErrors] = useState({});
    const isAdminRole = useCallback((roleName) => (roleName || "").trim().toLowerCase() === "admin", []);

    React.useEffect(() => {
        if (isEditMode && rolesData && rolesData.results) {
            const role = rolesData.results.find(r => String(r.documentId) === String(id));
            if (role) {
                if (isAdminRole(role.name)) {
                    navigate('/roles');
                    return;
                }
                setFormData({
                    documentId: role.documentId,
                    name: role.name || '',
                    description: role.description || '',
                    permissions: role.permissions || {},
                });
            }
        }
    }, [isEditMode, rolesData, id, navigate, isAdminRole]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = t('rolesAdd.roleNameRequired');
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };



    const handlePermissionsChange = (permissionsObj) => {
        setFormData(prev => ({ ...prev, permissions: permissionsObj }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }

        if (isEditMode) {
            updateRoleMutation.mutate({
                id: formData.documentId,
                data: {
                    name: formData.name,
                    description: formData.description,
                    permissions: formData.permissions, // send as object
                }
            });
        } else {
            createRoleMutation.mutate({
                name: formData.name,
                description: formData.description,
                permissions: formData.permissions, // send as object
            });
        }
    };

    const handleCancel = () => {
        navigate('/roles');
    };

    return (
        <div className={contentClassName}>
            <Theme2FormFieldsStyle css={rolesTheme2Css} />
            <Row>
                <Col md="12">
                    <Card className={formFieldsClassName || undefined}>
                        <CardHeader>
                            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between">
                                <CardTitle tag="h4" className="roles-add-card-title" style={{ margin: 0 }}>
                                    <i className="tim-icons icon-single-02" style={{ marginRight: '10px' }} />
                                    {isEditMode ? t('rolesAdd.editRole') : t('rolesAdd.addNewRole')}
                                </CardTitle>

                                {/* Desktop View Buttons */}
                                <div className="d-none d-lg-flex align-items-center">
                                    <Button
                                        color="secondary"
                                        outline
                                        size="sm"
                                        onClick={handleCancel}
                                        disabled={createRoleMutation.isPending || updateRoleMutation.isPending}
                                        style={{ marginRight: "10px" }}
                                    >
                                        {t('rolesAdd.cancel')}
                                    </Button>
                                    <Button
                                        style={{ ...activeColorStyle, color: 'white' }}
                                        size="sm"
                                        onClick={handleSubmit}
                                        disabled={createRoleMutation.isPending || updateRoleMutation.isPending}
                                    >
                                        {createRoleMutation.isPending || updateRoleMutation.isPending ? (
                                            <>
                                                <Spinner size="sm" className="mr-1" />
                                                {isEditMode ? t('rolesAdd.updating') : t('rolesAdd.creating')}
                                            </>
                                        ) : (
                                            <>
                                                <i className={isEditMode ? "tim-icons icon-pencil" : "tim-icons icon-simple-add"} style={{ marginRight: '8px' }} />
                                                {isEditMode ? t('rolesAdd.updateRole') : t('rolesAdd.createRole')}
                                            </>
                                        )}
                                    </Button>
                                </div>

                                {/* Mobile View Buttons */}
                                <div className="d-flex d-lg-none flex-column w-100 mt-3">
                                    <Button
                                        color="secondary"
                                        outline
                                        onClick={handleCancel}
                                        disabled={createRoleMutation.isPending || updateRoleMutation.isPending}
                                        className="mb-2 w-100"
                                        style={{ padding: '12px' }}
                                    >
                                        {t('rolesAdd.cancel')}
                                    </Button>
                                    <Button
                                        style={{ ...activeColorStyle, color: 'white', padding: '12px' }}
                                        onClick={handleSubmit}
                                        disabled={createRoleMutation.isPending || updateRoleMutation.isPending}
                                        className="w-100"
                                    >
                                        {createRoleMutation.isPending || updateRoleMutation.isPending ? (
                                            <>
                                                <Spinner size="sm" className="mr-1" />
                                                {isEditMode ? t('rolesAdd.updating') : t('rolesAdd.creating')}
                                            </>
                                        ) : (
                                            <>
                                                <i className={isEditMode ? "tim-icons icon-pencil" : "tim-icons icon-simple-add"} style={{ marginRight: '8px' }} />
                                                {isEditMode ? t('rolesAdd.updateRole') : t('rolesAdd.createRole')}
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardBody>
                            <Form onSubmit={handleSubmit} className={formFieldsClassName}>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for="name" className="form-control-label">
                                                {t('rolesAdd.roleName')} <span style={{ color: 'red' }}>*</span>
                                            </Label>
                                            <Input
                                                id="name"
                                                name="name"
                                                type="text"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                className={inputClassName(errors.name)}
                                                style={inputStyle}
                                                placeholder={t('rolesAdd.enterRoleName')}
                                                disabled={createRoleMutation.isPending || updateRoleMutation.isPending}
                                            />
                                            {errors.name && (
                                                <div className="invalid-feedback d-block">
                                                    {errors.name}
                                                </div>
                                            )}
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="12">
                                        <FormGroup>
                                            <Label for="description" className="form-control-label">
                                                {t('rolesAdd.description')}
                                            </Label>
                                            <Input
                                                id="description"
                                                name="description"
                                                type="textarea"
                                                rows="3"
                                                value={formData.description}
                                                onChange={handleInputChange}
                                                className={inputClassName(errors.description)}
                                                style={inputStyle}
                                                placeholder={t('rolesAdd.enterRoleDescription')}
                                                disabled={createRoleMutation.isPending || updateRoleMutation.isPending}
                                            />
                                            {errors.description && (
                                                <div className="invalid-feedback d-block">
                                                    {errors.description}
                                                </div>
                                            )}
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <RolePermissions
                                    roleDetail={isEditMode ? { permissions: formData.permissions } : undefined}
                                    isNewRole={!isEditMode}
                                    onPermissionsChange={handlePermissionsChange}
                                    selectedRole="custom"
                                    isTheme2={isTheme2}
                                />
                                {/* Bottom buttons removed for mobile-responsive consistency */}
                                <div className="d-none d-lg-block mt-4">
                                     {/* Keeping a small spacer for desktop if needed, though buttons are in header */}
                                </div>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default RolesAdd; 